import React from 'react';
import { useLocation } from 'react-router';
import {
  ProductCollectionSlice,
  ProductCollection,
  Breadcrumb as BreadcrumbType,
  ProductCollectionPath,
  ExpertCategoryContent,
} from '../../../../types';
import { AdvizedCarousel } from 'src/components/advized';
import HeaderBlock from '../header-block';
import Products from '../products';
import Description from '../description';
import Offers from '../offers';
import Filter from '../filter';
import Breadcrumb from '../breadcrumb';
import Navigation from '../navigation';
import SecondaryNavigation from '../secondary-navigation';
import { DealsContextWrapper } from '../products/deals-context';
import { ProductCollectionFilterContext } from '../../filter-tracking';
import { useViewportContext } from 'src/components/viewport/context';
import { hasOffset } from '../../utils/pagination';
import { FeaturedCategoryArticle } from 'src/components/featured-category-article';
import { plainText } from 'src/areas/editorial-content/components/slices/rich-text';
import { CategoryPageExpertToplist } from 'src/areas/expert/toplists/components/product-toplist';

import * as S from './styles';

type Props = {
  slice: ProductCollectionSlice;
  productCollection: ProductCollection;
};

const formatBreadcrumbs = (
  sliceItems: Array<BreadcrumbType>,
  categoryPaths: Array<ProductCollectionPath>
): Array<BreadcrumbType> => {
  return [
    ...categoryPaths
      .map(({ name, pathName }) => {
        return {
          name,
          pathName,
        };
      })
      .filter(c => !sliceItems.some(si => si.pathName === c.pathName)),
    ...sliceItems,
  ];
};

/**
 * Resolves a slice to the correct component by type.
 */
const Slice = ({ slice, productCollection }: Props) => {
  const { isTablet } = useViewportContext();
  const location = useLocation();
  const isPaginated = location && hasOffset(location.search);

  const shouldRenderExpertContent = !isPaginated;

  switch (slice.__typename) {
    case 'HeaderBlockSlice':
      return <HeaderBlock title={slice.title} pagination={slice.pagination} />;
    case 'DescriptionSlice':
      return (
        <Description
          productCollection={productCollection}
          description={slice.description}
        />
      );
    case 'ProductsSlice': {
      return (
        <DealsContextWrapper dealsItems={slice.dealsItems}>
          <Products
            id={slice.id}
            pageInfo={slice.pageInfo}
            products={slice.products}
            productsCount={slice.productsCount}
            storeCount={slice.storeCount}
            legalText={slice.legalText}
            availableSorts={slice.availableSorts}
            currentSort={slice.currentSort}
            maxHitsAmount={slice.maxHitsAmount}
            sortDirection={slice.sortDirection}
            propertyColumns={slice.propertyColumns}
            expert={productCollection.expert as ExpertCategoryContent}
            productCollection={productCollection}
            preferredViewMode={slice.preferredViewMode}
          />
        </DealsContextWrapper>
      );
    }
    case 'OffersSlice':
      return (
        <Offers
          id={slice.id}
          pageInfo={slice.pageInfo}
          offers={slice.offers}
          productsCount={slice.productsCount}
          storeCount={slice.storeCount}
          legalText={slice.legalText}
          availableSorts={slice.availableSorts}
          currentSort={slice.currentSort}
          maxHitsAmount={slice.maxHitsAmount}
        />
      );
    case 'FilterSlice':
      return (
        <Filter
          id={slice.id}
          sections={slice.sections}
          filters={slice.filters}
          productsCount={slice.productsCount}
        />
      );
    case 'BreadcrumbsSlice':
      return (
        <Breadcrumb
          id={slice.id}
          items={formatBreadcrumbs(slice.items, productCollection.categoryPath)}
          categoryIds={productCollection.categories?.map(
            category => category.id
          )}
        />
      );
    case 'NavigationSlice':
      return (
        <ProductCollectionFilterContext.Provider
          value={{ filterLayout: 'facet-navigation' }}
        >
          <Navigation
            id={slice.id}
            links={slice.links}
            property={slice.property}
            key={`${slice.id}_${
              Array.isArray(slice.links)
                ? slice.links.map(({ id }) => id).join(',')
                : ''
            }`}
          />
        </ProductCollectionFilterContext.Provider>
      );
    case 'FeaturedArticlesSlice':
      return (
        <>
          <S.GridWrapper>
            {slice.toplistArticles!.map(tl => {
              const descriptionData = tl?.description
                ? tl?.description!.reduce((acc, d) => {
                    acc.set(d.type, d.data);
                    return acc;
                  }, new Map())
                : null;
              return (
                <S.GridArticleWrapper key={tl?.id}>
                  <FeaturedCategoryArticle
                    expert={tl?.expert}
                    key={tl?.id}
                    pathName={tl?.headlineInfo.url || ''}
                    imageUrl={descriptionData && descriptionData?.get('image')}
                    title={tl?.title || ''}
                    preamble={descriptionData && descriptionData?.get('text')}
                    category={slice.expert?.category!}
                  />
                  {tl?.pjProducts && (
                    <CategoryPageExpertToplist products={tl?.pjProducts} />
                  )}
                </S.GridArticleWrapper>
              );
            })}
            {slice.featuredArticle && (
              <S.GridWrapper key={slice.id}>
                <S.GridArticleWrapper key={slice?.id}>
                  <FeaturedCategoryArticle
                    key={slice.id}
                    pathName={`/${slice.featuredArticle.pathName}`}
                    imageUrl={
                      slice.featuredArticle.data.featuredImage &&
                      slice.featuredArticle.data.featuredImage.url
                    }
                    title={plainText(slice.featuredArticle.data.title)}
                    preamble={plainText(
                      slice.featuredArticle.data.metaDescription
                    )}
                  />
                  <CategoryPageExpertToplist
                    isTopList={false}
                    products={
                      // @ts-ignore not sure how to resolve PrismicSlicePrimary type to Product type
                      slice?.featuredArticle.data.body[0].primary.products
                    }
                  />
                </S.GridArticleWrapper>
              </S.GridWrapper>
            )}
          </S.GridWrapper>
        </>
      );
    case 'SecondaryNavigationSlice':
      return (
        <>
          {shouldRenderExpertContent && (
            <AdvizedCarousel
              offset={2}
              isList={false}
              numberOfCards={isTablet ? 5 : 3}
              hideExperts={false}
              {...(productCollection.expert as any)}
            />
          )}
          <SecondaryNavigation
            id={slice.id}
            key={slice.id}
            productCollection={productCollection}
            navigationItems={slice.navigationItems}
          />
        </>
      );

    default:
      return null;
  }
};

export default Slice;

export const test = {
  formatBreadcrumbs,
};
